export const WEB_RC_PREFIX = "web/rc-";
export const WEB_RELEASE_PREFIX = "web/release-";

/**
 * The tags on a build's commit, from `git tag --points-at HEAD`, which prints one tag per line.
 */
export function parseBuildTags(raw: string): string[] {
  return raw.split(/\s+/).filter(Boolean);
}

/**
 * The highest-numbered tag with the prefix, comparing numbers numerically so `web/rc-288.10` follows `web/rc-288.9`.
 */
export function latestTag(tags: readonly string[], prefix: string): string | undefined {
  return tags
    .filter((tag) => tag.startsWith(prefix))
    .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
    .pop();
}

/**
 * The web release number a build reports to the version gate, or 0 when no web tag can be found.
 *
 * Prefers a tag on the build's own commit over the nearest one in its history. Tags are created only after a deploy
 * succeeds, so a production build finds its candidate tag on the commit, while a commit's first staging build finds
 * none and reports the previous release.
 *
 * @param buildTags - The tags on the build's commit.
 * @param nearestWebReleaseTag - The nearest `web/release-*` tag in the commit's history, or empty.
 * @param nearestWebRcTag - The nearest `web/rc-*` tag in the commit's history, or empty.
 */
export function releaseNumberOf(
  buildTags: readonly string[],
  nearestWebReleaseTag: string,
  nearestWebRcTag: string,
): number {
  return (
    tagNumber(latestTag(buildTags, WEB_RELEASE_PREFIX), WEB_RELEASE_PREFIX) ||
    tagNumber(latestTag(buildTags, WEB_RC_PREFIX), WEB_RC_PREFIX) ||
    tagNumber(nearestWebReleaseTag, WEB_RELEASE_PREFIX) ||
    tagNumber(nearestWebRcTag, WEB_RC_PREFIX) ||
    0
  );
}

/**
 * The number after the prefix, `288.1` for `web/rc-288.1`; 0 for a missing or unnumbered tag.
 */
function tagNumber(tag: string | undefined, prefix: string): number {
  return tag?.startsWith(prefix) ? Number(tag.slice(prefix.length)) || 0 : 0;
}
